import { useNavigate, Navigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { createHousehold } from '@/api/households'
import { currentHousehold } from '@/lib/currentHousehold'
import { track } from '@/lib/analytics'
import { ROUTES } from '@/routes/routes'
import { CURRENCY_OPTIONS, type CurrencyCode } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { ExploreSampleLink } from '@/components/sample/ExploreSampleLink'

const CURRENCY_CODES = CURRENCY_OPTIONS.map((c) => c.code) as [CurrencyCode, ...CurrencyCode[]]

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Household name is required')
    .max(60, 'Household name must be 60 characters or fewer'),
  currency: z.enum(CURRENCY_CODES),
})

type FormValues = z.infer<typeof schema>

export default function OnboardingPage() {
  const { data: user, isLoading } = useCurrentUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { currency: 'INR' } })

  if (isLoading) return null

  // Already has a household — bounce to dashboard
  if (user?.household) {
    return <Navigate to={ROUTES.APP.DASHBOARD} replace />
  }

  async function onSubmit(values: FormValues) {
    try {
      const created = await createHousehold(values.name, values.currency)
      track('household_created')
      currentHousehold.set(created.id)
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      navigate(ROUTES.APP.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        // Race condition — someone else already created it
        await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
        navigate(ROUTES.APP.DASHBOARD, { replace: true })
        return
      }
      setError('root', { message: 'Something went wrong. Please try again.' })
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-[var(--fl-surface-subtle)]">
      <div className="w-full max-w-md">
        <div className="bg-[var(--fl-surface)] rounded-2xl shadow-sm p-8 space-y-6">
          <div className="space-y-1">
            <h1 className="font-['Sora',system-ui,sans-serif] text-2xl font-semibold text-[var(--fl-heading)]">
              Welcome to LogMySpends
            </h1>
            <p className="text-sm text-[var(--fl-text-2)]">
              Create your household to get started. You can rename it any time from Settings.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="name" className="text-sm font-medium text-[var(--fl-heading)]">
                Household name
              </label>
              <Input
                id="name"
                {...register('name')}
                maxLength={60}
                autoFocus
                autoComplete="off"
                placeholder="e.g. The Garcias"
              />
              {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="currency" className="text-sm font-medium text-[var(--fl-heading)]">
                Currency
              </label>
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <Select
                    id="currency"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-label="Currency"
                  >
                    {CURRENCY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </div>

            {errors.root && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                {errors.root.message}
              </div>
            )}

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? 'Creating…' : 'Create household'}
            </Button>
          </form>

          <div className="text-center">
            <ExploreSampleLink lead="Just looking around?" label="Explore with sample data first" />
          </div>

          <p className="text-xs text-[var(--fl-text-3)] text-center">
            Have an invitation to join an existing household? Check your email for the invite link.
          </p>
        </div>
      </div>
    </div>
  )
}
